import React, { useState, useMemo } from 'react';
import {
  Paper,
  Stack,
  Title,
  Group,
  Combobox,
  TextInput,
  UnstyledButton,
  useCombobox
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { UseFormReturnType } from '@mantine/form';
import { SubscriberRegistration, mapCustomIndustry } from '../../../services/api';

interface ProfessionalInfoFormProps {
  form: UseFormReturnType<SubscriberRegistration>;
  industries: string[];
  onIndustryChange: (value: string | null) => void;
  resetInterestAndPosition: () => void;
  setInterestSuggestions: (value: string[]) => void;
  setAdditionalChips: (value: string[]) => void;
  isPositionConfirmed: boolean;
  setIsPositionConfirmed: (value: boolean) => void;
  confirmPosition: () => void;
}

export const ProfessionalInfoForm: React.FC<ProfessionalInfoFormProps> = ({
  form,
  industries,
  onIndustryChange,
  resetInterestAndPosition,
  setInterestSuggestions,
  isPositionConfirmed,
  setIsPositionConfirmed,
  confirmPosition
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isIndustryConfirmed, setIsIndustryConfirmed] = useState(false);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption()
  });

  const filteredIndustries = useMemo(() => {
    const industriesWithoutOther = industries.filter(industry => industry !== 'Andere');
    if (searchValue.trim() === '') {
      return industriesWithoutOther;
    }
    return industriesWithoutOther.filter(industry =>
      industry.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [industries, searchValue]);

  const handleIndustrySelect = (value: string) => {
    setSearchValue(value);
    const oldIndustry = form.values.industry;
    form.setFieldValue('industry', value);
    form.setFieldValue('originalIndustry', '');
    onIndustryChange(value);
    combobox.closeDropdown();
    setIsIndustryConfirmed(true);

    if (oldIndustry !== value) {
      resetInterestAndPosition();
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    combobox.openDropdown();

    if (isIndustryConfirmed) {
      setIsIndustryConfirmed(false);
    }

    const exactMatch = industries.find(industry => industry.toLowerCase() === value.toLowerCase());
    const oldIndustry = form.values.industry;
    let newIndustry = '';

    if (value.trim() && !exactMatch && filteredIndustries.length === 0) {
      newIndustry = 'Andere';
      form.setFieldValue('industry', newIndustry);
      onIndustryChange(newIndustry);
    } else if (exactMatch) {
      newIndustry = exactMatch;
      form.setFieldValue('industry', newIndustry);
      onIndustryChange(newIndustry);
      form.setFieldValue('originalIndustry', '');
    } else if (value.trim() === '') {
      newIndustry = '';
      form.setFieldValue('industry', newIndustry);
      onIndustryChange(newIndustry);
      form.setFieldValue('originalIndustry', '');
    }

    if (oldIndustry !== newIndustry) {
      resetInterestAndPosition();
    }
  };

  const confirmIndustry = async () => {
    const currentValue = searchValue.trim();
    if (currentValue) {
      const exactMatch = industries.find(
        industry => industry.toLowerCase() === currentValue.toLowerCase()
      );
      const oldIndustry = form.values.industry;

      if (exactMatch) {
        form.setFieldValue('industry', exactMatch);
        form.setFieldValue('originalIndustry', '');
        onIndustryChange(exactMatch);
        setIsIndustryConfirmed(true);

        if (oldIndustry !== exactMatch) {
          resetInterestAndPosition();
        }
      } else {
        setIsIndustryConfirmed(true);
        form.setFieldValue('originalIndustry', currentValue);

        try {
          const mappedIndustry = await mapCustomIndustry(currentValue, industries);
          form.setFieldValue('industry', mappedIndustry);
          onIndustryChange(mappedIndustry);
        } catch (error) {
          console.error('Fehler beim Mapping der Branche:', error);
          const fallbackIndustry = industries[0] || 'Technologie & IT';
          form.setFieldValue('industry', fallbackIndustry);
          onIndustryChange(fallbackIndustry);

          if (oldIndustry !== fallbackIndustry) {
            resetInterestAndPosition();
          }
        }
      }
    }
    combobox.closeDropdown();
  };

  const handleTabKey = () => {
    const currentValue = searchValue.trim();
    if (currentValue) {
      const exactMatch = industries.find(
        industry => industry.toLowerCase() === currentValue.toLowerCase()
      );
      const oldIndustry = form.values.industry;

      if (exactMatch) {
        form.setFieldValue('industry', exactMatch);
        form.setFieldValue('originalIndustry', '');
        onIndustryChange(exactMatch);
        setIsIndustryConfirmed(true);

        if (oldIndustry !== exactMatch) {
          resetInterestAndPosition();
        }
      } else {
        setIsIndustryConfirmed(true);
        form.setFieldValue('originalIndustry', currentValue);

        mapCustomIndustry(currentValue, industries)
          .then(mappedIndustry => {
            form.setFieldValue('industry', mappedIndustry);
            onIndustryChange(mappedIndustry);

            if (oldIndustry !== mappedIndustry) {
              resetInterestAndPosition();
            }
          })
          .catch(error => {
            console.error('Fehler beim Mapping der Branche:', error);
            const fallbackIndustry = industries[0] || 'Technologie & IT';
            form.setFieldValue('industry', fallbackIndustry);
            onIndustryChange(fallbackIndustry);

            if (oldIndustry !== fallbackIndustry) {
              resetInterestAndPosition();
            }
          });
      }
    }
    combobox.closeDropdown();
  };

  const handleBlur = () => {
    if (!isIndustryConfirmed && searchValue.trim() !== '') {
      confirmIndustry();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!isIndustryConfirmed && searchValue.trim() !== '') {
        confirmIndustry();
      }
    } else if (event.key === 'Tab') {
      if (!isIndustryConfirmed && searchValue.trim() !== '') {
        setTimeout(() => {
          handleTabKey();
        }, 0);
      }
    }
  };

  const handleFocus = () => {
    combobox.openDropdown();
    if (isIndustryConfirmed) {
      setIsIndustryConfirmed(false);
    }
  };

  return (
    <Paper p="xl" radius="md" withBorder>
      <Stack gap="md">
        <Title order={3} size="h4">
          Berufliche Informationen
        </Title>
        <Group grow align="flex-start" style={{ minHeight: '85px' }}>
          <div>
            <Combobox store={combobox} withinPortal={false} onOptionSubmit={handleIndustrySelect}>
              <Combobox.Target>
                <TextInput
                  required
                  label="Branche"
                  placeholder="Wählen Sie Ihre Branche oder geben Sie sie ein"
                  value={searchValue}
                  onChange={event => handleSearchChange(event.currentTarget.value)}
                  onClick={() => combobox.openDropdown()}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  error={form.errors.industry}
                  withAsterisk
                  data-path="industry"
                  rightSection={
                    isIndustryConfirmed ? (
                      <IconCheck size={18} style={{ color: 'green', marginRight: '10px' }} />
                    ) : searchValue.trim() ? (
                      <UnstyledButton
                        onClick={confirmIndustry}
                        style={{
                          color: '#228be6',
                          fontSize: '14px',
                          marginRight: '8px',
                          cursor: 'pointer'
                        }}
                      >
                        Bestätigen
                      </UnstyledButton>
                    ) : (
                      <Combobox.Chevron />
                    )
                  }
                  rightSectionPointerEvents={isIndustryConfirmed ? 'none' : 'auto'}
                  rightSectionWidth={isIndustryConfirmed ? 40 : searchValue.trim() ? 80 : 30}
                  styles={{
                    input: {
                      paddingRight: isIndustryConfirmed
                        ? '40px'
                        : searchValue.trim()
                          ? '80px'
                          : '30px'
                    }
                  }}
                />
              </Combobox.Target>

              <Combobox.Dropdown>
                <Combobox.Options mah={200} style={{ overflowY: 'auto' }}>
                  {filteredIndustries.length > 0 ? (
                    filteredIndustries.map(industry => (
                      <Combobox.Option value={industry} key={industry}>
                        {industry}
                      </Combobox.Option>
                    ))
                  ) : (
                    <Combobox.Empty>
                      {searchValue.trim()
                        ? `"${searchValue}" wird als individuelle Branche übernommen`
                        : 'Keine Branchen gefunden'}
                    </Combobox.Empty>
                  )}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          </div>
        </Group>

        <TextInput
          required
          label="Position"
          placeholder="z.B. Geschäftsführer, Entwickler, Marketing Manager"
          data-path="position"
          error={form.errors.position}
          withAsterisk
          {...form.getInputProps('position')}
          onChange={e => {
            if (isPositionConfirmed) {
              setIsPositionConfirmed(false);
              form.setFieldValue('interests', []);
              setInterestSuggestions([]);
            }
            form.setFieldValue('position', e.currentTarget.value);
          }}
          onBlur={() => {
            if (!isPositionConfirmed && form.values.position.trim() !== '') {
              confirmPosition();
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !isPositionConfirmed && form.values.position.trim() !== '') {
              e.preventDefault();
              confirmPosition();
            }
          }}
          rightSection={
            isPositionConfirmed ? (
              <IconCheck size={18} style={{ color: 'green', marginRight: '10px' }} />
            ) : (
              <UnstyledButton
                onClick={confirmPosition}
                disabled={!form.values.position.trim()}
                style={{
                  color: '#228be6',
                  fontSize: '14px',
                  marginRight: '8px',
                  opacity: !form.values.position.trim() ? 0.5 : 1,
                  cursor: !form.values.position.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                Bestätigen
              </UnstyledButton>
            )
          }
          rightSectionWidth={isPositionConfirmed ? 40 : 80}
          styles={{
            input: {
              paddingRight: isPositionConfirmed ? '40px' : '80px'
            }
          }}
        />
      </Stack>
    </Paper>
  );
};
